// AGC: Single source of truth for task-category → machinery rules.
// AGC: Consumed by frontend TaskDetail picker, backend taskValidator, admin TaskRecommendationsAdmin.
// AGC: Human-readable companion: docs/TASK_ACTIVITY_RULES.md

// AGC: MachineCategory duplicated here to avoid circular imports between shared ↔ app.
// AGC: Keep in sync with packages/app/src/types/machine.ts:MachineCategory.
export type MachineCategory =
  | 'trator'
  | 'colheitadeira'
  | 'plantadeira'
  | 'pulverizador'
  | 'distribuidor'
  | 'implemento'
  | 'irrigacao'
  | 'transporte'
  | 'outros'

export type ActivityMode = 'standalone' | 'combo-required' | 'either' | 'any'

export interface ActivityRule {
  taskCategory: string
  mode: ActivityMode
  // AGC: Each inner array is one valid combo.
  // AGC: 'either' has multiple single/multi-element arrays (user picks one).
  // AGC: 'combo-required' has exactly one multi-element array (all slots required).
  // AGC: 'standalone' has one single-element array (one slot).
  // AGC: 'any' is empty (no filter — show whole fleet).
  allowedCombos: MachineCategory[][]
  // AGC: Allow trator as degraded fallback when ideal combo can't be satisfied by the fleet.
  tratorFallback: boolean
}

export const ACTIVITY_RULES: Record<string, ActivityRule> = {
  plantio: {
    taskCategory: 'plantio',
    mode: 'combo-required',
    allowedCombos: [['trator', 'plantadeira']],
    tratorFallback: false,
  },
  pulverizacao: {
    taskCategory: 'pulverizacao',
    mode: 'either',
    allowedCombos: [['pulverizador'], ['trator', 'pulverizador']],
    tratorFallback: true,
  },
  colheita: {
    taskCategory: 'colheita',
    mode: 'standalone',
    allowedCombos: [['colheitadeira']],
    tratorFallback: true,
  },
  adubacao: {
    taskCategory: 'adubacao',
    mode: 'either',
    allowedCombos: [['distribuidor'], ['trator', 'distribuidor']],
    tratorFallback: true,
  },
  irrigacao: {
    taskCategory: 'irrigacao',
    mode: 'standalone',
    allowedCombos: [['irrigacao']],
    tratorFallback: false,
  },
  manutencao: {
    taskCategory: 'manutencao',
    mode: 'any',
    allowedCombos: [],
    tratorFallback: false,
  },
  monitoramento: {
    taskCategory: 'monitoramento',
    mode: 'any',
    allowedCombos: [],
    tratorFallback: false,
  },
  outros: {
    taskCategory: 'outros',
    mode: 'any',
    allowedCombos: [],
    tratorFallback: false,
  },
  sorteio: {
    taskCategory: 'sorteio',
    mode: 'any',
    allowedCombos: [],
    tratorFallback: false,
  },
}

/**
 * AGC: Return the rule for a task category, falling back to 'outros' (mode: 'any').
 */
// AGC: Fallback rule — returned when category is unknown. Also the default for
// AGC: 'outros'/etc. so callers always get a valid rule shape.
const FALLBACK_RULE: ActivityRule = { taskCategory: 'outros', mode: 'any', allowedCombos: [], tratorFallback: false }

export function getRule(taskCategory: string): ActivityRule {
  return ACTIVITY_RULES[taskCategory?.toLowerCase()] ?? FALLBACK_RULE
}

/**
 * AGC: Normalise a machine category string to the canonical MachineCategory enum.
 * AGC: Real-world fleet data mixes English and Portuguese ("tractor" vs "trator",
 * AGC: "sprayer" vs "pulverizador"). This helper maps synonyms so picker filters and
 * AGC: validators compare apples-to-apples. Unknown values fall through to 'outros'.
 */
export function normalizeMachineCategory(raw: string | null | undefined): MachineCategory {
  const s = String(raw ?? '').toLowerCase().trim()
  if (!s) return 'outros'
  const synonyms: Record<string, MachineCategory> = {
    // AGC: English → Portuguese canonical
    tractor: 'trator',
    trator: 'trator',
    harvester: 'colheitadeira',
    combine: 'colheitadeira',
    colheitadeira: 'colheitadeira',
    planter: 'plantadeira',
    seeder: 'plantadeira',
    plantadeira: 'plantadeira',
    semeadora: 'plantadeira',
    sprayer: 'pulverizador',
    pulverizador: 'pulverizador',
    pulverizador_autopropelido: 'pulverizador',
    pulverizador_arrasto: 'pulverizador',
    'self_propelled_sprayer': 'pulverizador',
    spreader: 'distribuidor',
    fertilizer_spreader: 'distribuidor',
    distribuidor: 'distribuidor',
    implemento: 'implemento',
    implement: 'implemento',
    irrigacao: 'irrigacao',
    irrigation: 'irrigacao',
    transporte: 'transporte',
    transport: 'transporte',
    truck: 'transporte',
    outros: 'outros',
    other: 'outros',
    // AGC: Argentine Spanish → Portuguese canonical. The AR farm agent speaks Spanish but
    // AGC: MUST store canonical category IDs (cross-country data consistency, backend-compatibility).
    // AGC: Without these, AR machines silently fall through to 'outros'.
    cosechadora: 'colheitadeira',
    sembradora: 'plantadeira',
    pulverizadora: 'pulverizador',
    fumigadora: 'pulverizador',
    fertilizadora: 'distribuidor',
    esparcidora: 'distribuidor',
    abonadora: 'distribuidor',
    riego: 'irrigacao',
    camion: 'transporte',
    'camión': 'transporte',
    otros: 'outros',
  }
  return synonyms[s] ?? 'outros'
}

/**
 * AGC: Flatten allowedCombos into a unique set of eligible MachineCategories for picker filtering.
 * AGC: Used by frontend picker and backend validator to decide if a given MachineInstance is acceptable.
 * AGC: tratorFallback machines are included so the user can always pick a tractor as degraded fallback.
 */
export function eligibleCategories(taskCategory: string): Set<MachineCategory> {
  const rule = getRule(taskCategory)
  const set = new Set<MachineCategory>()
  for (const combo of rule.allowedCombos) {
    for (const cat of combo) {
      set.add(cat)
    }
  }
  if (rule.tratorFallback) {
    set.add('trator')
  }
  return set
}
